import type { ScheduleActivityItem } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_activity.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { getUser } from '@frontend/auth-helpers';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { activitySourceTypeLabels, activityTypeLabels } from './constants';
import { formatAppliedFilterDate } from './filters/helpers';
import type { FilterOptions, FilterState } from './filters/types';
import type { ActivityType, ActivitySourceType } from './types';

export const useScheduleActivityTracking = (locationIds: string[], filterOptions: FilterOptions) => {
  const { selectedOrgId } = useSchedulingLocationInfo();
  const userId = getUser()?.userID || '';

  const trackScheduleActivityViewedEvent = () => {
    SchedulerV3.trackPendoEvent('schedule-activity-viewed', {
      organizationId: selectedOrgId,
      locationIds,
      userId,
    });
  };

  const trackFiltersApplied = (filters: FilterState) => {
    const activityTypeLabelMap = new Map(filterOptions.activityTypes.map((item) => [item.value, item.label]));
    const appointmentTypeLabelMap = new Map(filterOptions.appointmentTypes.map((item) => [item.value, item.label]));
    const sourceTypeLabelMap = new Map(filterOptions.sourceTypes.map((item) => [item.value, item.label]));
    const statusTypeLabelMap = new Map(filterOptions.statusTypes.map((item) => [item.value, item.label]));

    const appliedFilters = [
      {
        filterType: 'event',
        filterValue: filters.activityTypes.map((value) => activityTypeLabelMap.get(value) ?? value).join(','),
      },
      {
        filterType: 'appointmentType',
        filterValue: filters.appointmentTypeIds.map((value) => appointmentTypeLabelMap.get(value) ?? value).join(','),
      },
      {
        filterType: 'source',
        filterValue: filters.sourceTypes.map((value) => sourceTypeLabelMap.get(value) ?? value).join(','),
      },
      {
        filterType: 'status',
        filterValue: filters.statusTypes.map((value) => statusTypeLabelMap.get(value) ?? value).join(','),
      },
      {
        filterType: 'timeFrame',
        filterValue: [filters.startDate, filters.endDate].filter(Boolean).map(formatAppliedFilterDate).join(' - '),
      },
    ];

    SchedulerV3.trackPendoEvent('schedule-activity-filtered', {
      appliedFilters: appliedFilters
        .filter(({ filterValue }) => filterValue)
        .map(({ filterType, filterValue }) => `${filterType}:${filterValue}`)
        .join(';'),
    });
  };

  const trackRowClicked = (row: ScheduleActivityItem) => {
    const sourceDetailByType = {
      BOOKING_SITE: 'online_scheduling',
      AI_VOICE: 'ai_receptionist',
      AI_TEXT: 'ai_receptionist',
      STAFF_UI: 'staff',
      WAITLIST: 'waitlist',
      PMS: 'schedule_request',
      OTHER: 'schedule_request',
    } as const;

    SchedulerV3.trackPendoEvent('schedule-activity-row-clicked', {
      activityId: row.id ?? '',
      activityType: activityTypeLabels[row.activityType as ActivityType] ?? row.activityType,
      currentStatus: row.currentStatus ?? '',
      source: activitySourceTypeLabels[row.activitySourceType as ActivitySourceType] ?? row.activitySourceType,
      sourceDetail: sourceDetailByType[row.activitySourceType as keyof typeof sourceDetailByType] ?? '',
    });
  };

  return { trackScheduleActivityViewedEvent, trackFiltersApplied, trackRowClicked };
};
